import { Button, Input, Textarea } from '@cian/ui-kit';
import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import styles from './FeedbackModalForm.css';
import { trackSubmitFeedbackForm } from '../../../../tracking';
import { IFeedbackDataRequest, IFeedbackDataResponseSchema } from '../../../../types';
import { TooltipOptional } from '../../../TooltipOptional';

export interface IFeedbackModalFormProps {
  sendFeedbackAction(params: IFeedbackDataRequest): Promise<IFeedbackDataResponseSchema>;
  showSuccessAlert(): void;
}

export interface IFeedbackModalFormState {
  name: string;
  email: string;
  companyName: string;
  phone: string;
  comment: string;
  validated: boolean;

  companyNameValidatorMessage?: string;
  contactValidatorMessage?: string;
  emailValidatorMessage?: string;
  phoneValidatorMessage?: string;
  serverErrorMessage?: string;
}

const validatorMessages: {
  [key: string]: string;
} = {
  companyName: 'Укажите название компании',
  phone: 'Укажите телефон',
};

export class FeedbackModalForm extends React.Component<IFeedbackModalFormProps, IFeedbackModalFormState> {
  public constructor(props: IFeedbackModalFormProps) {
    super(props);

    this.state = { comment: '', companyName: '', email: '', name: '', phone: '', validated: false };
  }

  public render() {
    const {
      comment,
      companyName,
      email,
      name,
      phone,
      validated,
      companyNameValidatorMessage,
      phoneValidatorMessage,
      serverErrorMessage,
    } = this.state;

    return (
      <div tabIndex={0} className={styles['modal-container']}>
        <div className={styles['modal-content_area']}>
          <div className={styles['modal-header']}>
            <h1>Заявка на привязку объектов к коттеджным посёлкам</h1>
          </div>
          <div className={styles['modal-input_group']}>
            <div className={styles['modal-label']}>Название компании</div>
            <div className={styles['modal-value']}>
              <TooltipOptional
                placement="bottom-start"
                title={companyNameValidatorMessage || ''}
                visible={!!companyNameValidatorMessage}
              >
                <Input
                  autoFocus={true}
                  onChange={(_, value) => this.onCompanyNameValueChange(value)}
                  value={companyName || ''}
                  required={true}
                  invalid={companyName.length === 0 && validated}
                />
              </TooltipOptional>
            </div>
          </div>
          <div {...mergeStyles(styles['modal-input_group'], styles['modal-name_container'])}>
            <div className={styles['modal-label']}>Имя и фамилия</div>
            <div className={styles['modal-value']}>
              <Input onChange={(_, value) => this.setState({ name: value })} value={name || ''} />
            </div>
          </div>
          <div className={styles['modal-input_group']}>
            <div className={styles['modal-label']}>Номер телефона</div>
            <div {...mergeStyles(styles['modal-value'], styles['modal-phone'])}>
              <TooltipOptional
                placement="bottom-start"
                title={phoneValidatorMessage || ''}
                visible={!!phoneValidatorMessage}
              >
                <Input
                  onChange={(_, value) => this.onPhoneValueChange(value)}
                  value={phone || ''}
                  required={true}
                  invalid={phone.length === 0 && validated}
                  placeholder="+7"
                />
              </TooltipOptional>
            </div>
          </div>
          <div className={styles['modal-input_group']}>
            <div className={styles['modal-label']}>Электронная почта</div>
            <div {...mergeStyles(styles['modal-value'], styles['modal-email'])}>
              <Input value={email || ''} onChange={(_, value) => this.setState({ email: value })} />
            </div>
          </div>
          <div {...mergeStyles(styles['modal-input_group'], styles['modal-input--multiline'])}>
            <div className={styles['modal-label']}>Комментарий</div>
            <div {...mergeStyles(styles['modal-value'], styles['modal-comment_container'])}>
              <Textarea
                onChange={(_, value) => this.onCommentValueChange(value)}
                value={comment || ''}
                minRows={4}
                maxRows={4}
              />
            </div>
          </div>
        </div>
        <div className={styles['modal-accept_area']}>
          <TooltipOptional placement="right" title={serverErrorMessage || ''} visible={!!serverErrorMessage}>
            <Button onClick={() => this.sendRequest()} type="submit">
              Отправить
            </Button>
          </TooltipOptional>
        </div>
      </div>
    );
  }

  private onCommentValueChange = (value: string) => {
    this.setState({
      comment: value,
    });
  };

  private onCompanyNameValueChange(companyName: string) {
    if (!this.state.validated) {
      this.setState({ companyName });

      return;
    }

    this.setState({
      companyName,
      companyNameValidatorMessage: companyName.length === 0 ? validatorMessages.companyName : undefined,
    });
  }

  private onPhoneValueChange(phone: string) {
    if (!this.state.validated) {
      this.setState({ phone });

      return;
    }

    this.setState({
      phone,
      phoneValidatorMessage: phone.length === 0 ? validatorMessages.phone : undefined,
    });
  }

  private handleServerError = () => {
    this.setState({ serverErrorMessage: 'Ошибка. Попробуйте ещё раз' });
  };

  private async sendRequest() {
    const { sendFeedbackAction, showSuccessAlert } = this.props;
    this.setState({ validated: true, serverErrorMessage: undefined });

    if (!this.validateRequiredFields()) {
      return;
    }
    trackSubmitFeedbackForm();

    try {
      const { status } = await sendFeedbackAction(this.prepareMessage());
      if (status !== 'ok') {
        return this.handleServerError();
      }

      showSuccessAlert();
    } catch (e) {
      this.handleServerError();
    }
  }

  private validateRequiredFields(): boolean {
    const { companyName, phone } = this.state;

    let isValid = true;

    const companyNameValidatorMessage = companyName && companyName.length ? undefined : validatorMessages.companyName;
    const phoneValidatorMessage = phone && phone.length ? undefined : validatorMessages.phone;

    if (companyNameValidatorMessage || phoneValidatorMessage) {
      isValid = false;
    }

    this.setState({
      companyNameValidatorMessage,
      phoneValidatorMessage,
    });

    return isValid;
  }

  private prepareMessage() {
    const { companyName, comment, phone, name, email } = this.state;

    const message = `
        Название компании
        ${companyName}
        Имя и фамилия
        ${name}
        Телефон
        ${phone}
        Email
        ${email}
        Комментарий
        ${comment}
    `;

    return {
      source: 'kp_request',
      text: message,
      url: window.location.href,
    };
  }
}
